/**
 * Generate a landscape PDF validation report in tabular format (Dizzaroo / export / AI validations).
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { enrichValidationResults, formatValidationErrorMessage } from '../constants/validationCheckCatalog';

const STATUS_COLORS = {
  passed: [46, 125, 50],
  failed: [198, 40, 40],
  skipped: [237, 108, 2],
};

/**
 * jsPDF's built-in Helvetica/Times fonts only support WinAnsiEncoding (Latin-1) \u2014 there's
 * no embedded Unicode font in this export (jsPDF needs a raw TTF/OTF; the only font asset
 * in this repo, @fontsource/noto-sans-math, ships WOFF/WOFF2 only, which jsPDF can't consume).
 * Math-operator glyphs like \u2265/\u2264/\u2260/\u2248 fall outside WinAnsi and render as garbled/wrong
 * characters instead of failing cleanly, so normalize them to their ASCII equivalents here \u2014
 * same fallback the app's other (react-pdf) font module documents for this exact glyph gap.
 */
const sanitizePdfText = (value) =>
  String(value ?? '')
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/\u2026/g, '...')
    .replace(/\u00A0/g, ' ')
    .replace(/\u2192/g, '->')
    .replace(/\u2190/g, '<-')
    .replace(/\u2265/g, '>=')
    .replace(/\u2264/g, '<=')
    .replace(/\u2260/g, '!=')
    .replace(/\u2248/g, '~=')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim() || '-';

const buildDetailText = (check) => {
  if (check.status === 'passed') {
    return check.successDetail || 'Passed - meets pass criteria.';
  }
  if (check.failureDetail && check.failureDetail.length > 0) {
    return check.failureDetail
      .map((line, i) => `${i + 1}. ${formatValidationErrorMessage(line)}`)
      .join('\n');
  }
  if (check.status === 'skipped') return 'Skipped - prerequisites not met.';
  return 'Failed - no specific findings recorded.';
};

const sortForReport = (items) => {
  const order = { failed: 0, skipped: 1, passed: 2 };
  return [...items].sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3));
};

/**
 * @param {Object} options
 * @param {Array} options.results - validation result rows
 * @param {string} options.reportTitle
 * @param {string} [options.studyId] - Sponsor Protocol Identifier (legacy param name)
 * @param {string} [options.trialAcronym]
 * @param {string} [options.sponsorName]
 * @param {string} [options.projectName]
 * @param {string} [options.protocolNumber]
 * @param {number} [options.sectionCount]
 */
export const generateValidationReportPdf = ({
  results = [],
  reportTitle = 'Validation Report',
  studyId = '',
  trialAcronym = '',
  sponsorName = '',
  projectName = '',
  protocolNumber = '',
  sectionCount = 0,
} = {}) => {
  const enriched = sortForReport(enrichValidationResults(results));
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36;
  const contentWidth = pageWidth - 2 * margin;
  let y = 68;

  const passed = enriched.filter((r) => r.status === 'passed').length;
  const failed = enriched.filter((r) => r.status === 'failed').length;
  const skipped = enriched.filter((r) => r.status === 'skipped').length;
  const generatedAt = new Date().toLocaleString();

  const colNo = 28;
  const colCheck = 138;
  const colOutcome = 76;
  const colMethod = 76;
  const colHow = 154;
  const colPass = 132;
  const colFindings = contentWidth - (colNo + colCheck + colOutcome + colMethod + colHow + colPass);

  const tableColumnStyles = {
    0: { cellWidth: colNo, halign: 'center', valign: 'top' },
    1: { cellWidth: colCheck, valign: 'top', fontStyle: 'bold' },
    2: { cellWidth: colOutcome, halign: 'center', valign: 'top', fontStyle: 'bold' },
    3: { cellWidth: colMethod, halign: 'center', valign: 'top' },
    4: { cellWidth: colHow, valign: 'top' },
    5: { cellWidth: colPass, valign: 'top' },
    6: { cellWidth: colFindings, valign: 'top' },
  };

  const baseTableStyles = {
    font: 'helvetica',
    fontSize: 8,
    cellPadding: { top: 5, right: 4, bottom: 5, left: 4 },
    overflow: 'linebreak',
    lineColor: [200, 200, 200],
    lineWidth: 0.5,
    textColor: [30, 30, 30],
    minCellHeight: 14,
  };

  // Cover header band
  doc.setFillColor(106, 27, 154);
  doc.rect(0, 0, pageWidth, 56, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(sanitizePdfText(reportTitle), margin, 28);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Dizzaroo Validation Report - Landscape Tabular Format', margin, 44);
  doc.setTextColor(0, 0, 0);

  // Executive summary table
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(106, 27, 154);
  doc.text('EXECUTIVE SUMMARY', margin, y);
  y += 12;

  const summaryRows = [
    ['Report', sanitizePdfText(reportTitle)],
    ['Generated', sanitizePdfText(generatedAt)],
    ...(trialAcronym || projectName ? [['Trial Acronym', sanitizePdfText(trialAcronym || projectName)]] : []),
    ...(studyId ? [['Sponsor Protocol Identifier', sanitizePdfText(studyId)]] : []),
    ...(sponsorName ? [['Sponsor Name', sanitizePdfText(sponsorName)]] : []),
    ...(protocolNumber ? [['Protocol', sanitizePdfText(protocolNumber)]] : []),
    ...(sectionCount ? [['Sections reviewed', String(sectionCount)]] : []),
    ['Total checks', String(enriched.length)],
    ['Passed', String(passed)],
    ['Failed', String(failed)],
    ['Skipped', String(skipped)],
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    tableWidth: contentWidth,
    head: [['Field', 'Value']],
    body: summaryRows,
    theme: 'grid',
    styles: {
      ...baseTableStyles,
      fontSize: 9,
      cellPadding: 4,
    },
    headStyles: {
      fillColor: [106, 27, 154],
      textColor: 255,
      fontStyle: 'bold',
      halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 140, fontStyle: 'bold', fillColor: [245, 245, 245] },
      1: { cellWidth: contentWidth - 140 },
    },
    alternateRowStyles: { fillColor: [252, 252, 252] },
  });

  y = doc.lastAutoTable.finalY + 16;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(25, 118, 210);
  doc.text(`VALIDATION CHECKS (${enriched.length})`, margin, y);
  y += 10;
  doc.setTextColor(0, 0, 0);

  const bodyRows = enriched.map((check, index) => [
    String(index + 1),
    sanitizePdfText(check.label || check.id),
    sanitizePdfText(check.outcome),
    sanitizePdfText(check.methodologyLabel),
    sanitizePdfText(check.howChecked),
    sanitizePdfText(check.passCriteria),
    sanitizePdfText(buildDetailText(check)),
  ]);

  const rowStatuses = enriched.map((check) => check.status);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin, bottom: 32 },
    tableWidth: contentWidth,
    head: [
      [
        '#',
        'Validation Check',
        'Outcome',
        'Method',
        'How Checked',
        'Pass Criteria',
        'Findings / Result',
      ],
    ],
    body: bodyRows,
    showHead: 'everyPage',
    styles: baseTableStyles,
    headStyles: {
      fillColor: [25, 118, 210],
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      minCellHeight: 20,
    },
    columnStyles: tableColumnStyles,
    alternateRowStyles: { fillColor: [248, 249, 250] },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      const status = rowStatuses[data.row.index];
      if (data.column.index === 2 && STATUS_COLORS[status]) {
        data.cell.styles.textColor = STATUS_COLORS[status];
        data.cell.styles.fontStyle = 'bold';
      }
      if (data.column.index === 6 && status === 'failed') {
        data.cell.styles.textColor = STATUS_COLORS.failed;
      }
      if (data.column.index === 6 && status === 'skipped') {
        data.cell.styles.textColor = STATUS_COLORS.skipped;
      }
      if (data.column.index === 6 && status === 'passed') {
        data.cell.styles.textColor = STATUS_COLORS.passed;
      }
    },
  });

  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Dizzaroo Validation Report - ${sanitizePdfText(reportTitle)} - Page ${p} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 18,
      { align: 'center' }
    );
  }

  const safeStudy = (studyId || 'study').replace(/[^\w.-]+/g, '_');
  const filename = `validation-report-${safeStudy}-${Date.now()}.pdf`;
  doc.save(filename);
  return filename;
};

export default {
  generateValidationReportPdf,
  enrichValidationResults,
};
